import axios, { AxiosInstance, AxiosError } from 'axios';
import {
  LoginRequest,
  LoginResponse,
  APIError,
  Company,
  Batch,
  BatchCreate,
  BatchProgress,
  MaiaFile,
  FilePreview,
  ExcelEditorData,
  SaveChangeRequest,
  BankStatementData,
  ActivityLog,
  NominalCode,
  CreateNominalCodeRequest,
  CreateCompanyRequest,
  UpdateCompanyRequest,
  Knowledge,
  KnowledgeListResponse,
  CreateKnowledgeRequest,
  UpdateKnowledgeRequest,
  AdminUser,
  CreateUserRequest,
  UpdateUserRequest,
  Counterparty,
  CreateCounterpartyRequest,
  UpdateCounterpartyRequest,
  CounterpartyType,
  Supplier,
  CreateSupplierRequest,
  UpdateSupplierRequest
} from '../types/api';

const AUTH_TOKEN_KEY = 'maia_auth_token';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor - add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<APIError>) => {
    if (error.response?.status === 401) {
      localStorage.removeItem(AUTH_TOKEN_KEY);
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ============================================================================
// Authentication
// ============================================================================

export const login = async (credentials: LoginRequest): Promise<LoginResponse> => {
  const response = await apiClient.post<LoginResponse>('/login', credentials);
  localStorage.setItem(AUTH_TOKEN_KEY, response.data.access_token);
  return response.data;
};

export const logout = (): void => {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  window.location.href = '/login';
};

export const isAuthenticated = (): boolean => {
  return !!localStorage.getItem(AUTH_TOKEN_KEY);
};

export const getToken = (): string | null => {
  return localStorage.getItem(AUTH_TOKEN_KEY);
};

export const getCurrentUser = async () => {
  const response = await apiClient.get('/me');
  return response.data;
};

// ============================================================================
// Companies
// ============================================================================

export const getCompanies = async (): Promise<Company[]> => {
  const response = await apiClient.get<Company[]>('/companies');
  return response.data;
};

export const getCompany = async (companyId: number): Promise<Company> => {
  const response = await apiClient.get<Company>(`/company/${companyId}/info`);
  return response.data;
};

// ============================================================================
// Batches
// ============================================================================

export const getBatches = async (params?: {
  company_id?: number;
  status?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}): Promise<Batch[]> => {
  const response = await apiClient.get<Batch[]>('/batches', { params });
  return response.data;
};

export const createBatch = async (data: BatchCreate): Promise<{ id: number; message: string }> => {
  const response = await apiClient.post('/batches', data);
  return response.data;
};

export const getBatchProgress = async (batchId: number): Promise<BatchProgress> => {
  const response = await apiClient.get<BatchProgress>(`/batches/${batchId}/progress`);
  return response.data;
};

export const updateBatchStatus = async (batchId: number, status: string): Promise<void> => {
  await apiClient.patch(`/batches/${batchId}/status`, null, { params: { status } });
};

// ============================================================================
// Files
// ============================================================================

export const getBatchFiles = async (batchId: number): Promise<MaiaFile[]> => {
  const response = await apiClient.get<MaiaFile[]>('/batch-files', {
    params: { batchId }
  });
  return response.data;
};

export const uploadFile = async (
  file: File,
  batchId?: number,
  uploadToken?: string
): Promise<{ id: number; filename: string; path: string; size: number }> => {
  const formData = new FormData();
  formData.append('file', file);
  if (batchId) formData.append('batch_id', batchId.toString());
  if (uploadToken) formData.append('upload_token', uploadToken);

  const response = await apiClient.post('/upload-file', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const getFilePreviews = async (fileId: number): Promise<FilePreview[]> => {
  const response = await apiClient.get<FilePreview[]>(`/file/${fileId}/preview`);
  return response.data;
};

export const updateFileType = async (fileId: number, fileType: string): Promise<void> => {
  await apiClient.patch(`/files/${fileId}/type`, null, { params: { file_type: fileType } });
};

// ============================================================================
// Excel Editor (Export Records)
// ============================================================================

export const getExcelEditorData = async (
  batchId: number,
  fileId?: number,
  displayWholeBatch?: boolean
): Promise<ExcelEditorData> => {
  const response = await apiClient.get<ExcelEditorData>('/excel-editor', {
    params: { batchId, fileId, displayWholeBatch }
  });
  return response.data;
};

export const saveExcelChanges = async (data: SaveChangeRequest): Promise<{ status: string }> => {
  // Note: This endpoint is at /excel/save-changes (not /api/excel/save-changes)
  const response = await axios.post('/excel/save-changes', data, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem(AUTH_TOKEN_KEY)}`
    }
  });
  return response.data;
};

// ============================================================================
// Bank Statements
// ============================================================================

export const getBankStatementData = async (
  companyId: number,
  params?: {
    account_id?: number;
    date_from?: string;
    date_to?: string;
    transaction_type?: string;
    currency?: string;
  }
): Promise<BankStatementData> => {
  const response = await apiClient.get<BankStatementData>(
    `/bank-statements/${companyId}/data`,
    { params }
  );
  return response.data;
};

export const saveBankChanges = async (
  changes: Array<{
    transaction_id: number;
    field: string;
    old_value: string;
    new_value: string;
  }>
): Promise<{ success: boolean; updated_count: number }> => {
  const response = await apiClient.post('/bank-statements/save-changes', { changes });
  return response.data;
};

export const linkFilesToTransaction = async (
  transactionId: number,
  fileIds: number[]
): Promise<{ success: boolean }> => {
  const response = await apiClient.post(
    `/bank-statements/transactions/${transactionId}/files`,
    { file_ids: fileIds }
  );
  return response.data;
};

export const unlinkFileFromTransaction = async (
  transactionId: number,
  fileId: number
): Promise<{ success: boolean }> => {
  const response = await apiClient.delete(
    `/bank-statements/transactions/${transactionId}/files/${fileId}`
  );
  return response.data;
};

// ============================================================================
// Activity Log
// ============================================================================

export const getFileActivityLog = async (
  fileId: number
): Promise<{ success: boolean; data: ActivityLog[] }> => {
  const response = await apiClient.get(`/files/${fileId}/activity-log`);
  return response.data;
};

export interface ActivityFilters {
  limit?: number;
  offset?: number;
  agent_name?: string;
  activity_type?: string;
  command_name?: string;
  date_from?: string;
  date_to?: string;
}

export interface ActivityResponse {
  success: boolean;
  data: ActivityLog[];
  total: number;
  limit: number;
  offset: number;
}

export interface ActivityFilterOptions {
  agents: string[];
  activity_types: string[];
  commands: string[];
}

export const getRecentActivity = async (
  filters?: ActivityFilters
): Promise<ActivityResponse> => {
  const response = await apiClient.get('/activity-log/recent', {
    params: filters
  });
  return response.data;
};

export const getActivityFilters = async (): Promise<ActivityFilterOptions> => {
  const response = await apiClient.get('/activity-log/filters');
  return response.data;
};

// ============================================================================
// Admin
// ============================================================================

export const getUsers = async () => {
  const response = await apiClient.get('/admin/users');
  return response.data;
};

export const createUser = async (data: {
  username: string;
  password: string;
  email?: string;
  full_name?: string;
  role?: string;
}) => {
  const response = await apiClient.post('/admin/users', data);
  return response.data;
};

// ============================================================================
// Company Management
// ============================================================================

export const createCompany = async (data: CreateCompanyRequest): Promise<{ id: number }> => {
  const response = await apiClient.post('/companies', data);
  return response.data;
};

export const updateCompany = async (
  companyId: number,
  data: UpdateCompanyRequest
): Promise<{ message: string }> => {
  const response = await apiClient.put(`/companies/${companyId}`, null, { params: data });
  return response.data;
};

export const deleteCompany = async (companyId: number): Promise<{ message: string }> => {
  const response = await apiClient.delete(`/companies/${companyId}`);
  return response.data;
};

// ============================================================================
// Nominal Codes
// ============================================================================

export const getNominalCodes = async (companyId?: number): Promise<NominalCode[]> => {
  const response = await apiClient.get<NominalCode[]>('/nominal-codes', {
    params: companyId ? { company_id: companyId } : undefined
  });
  return response.data;
};

export const getCompanyNominalCodes = async (companyId: number): Promise<NominalCode[]> => {
  const response = await apiClient.get<NominalCode[]>(`/companies/${companyId}/nominal-codes`);
  return response.data;
};

export const createNominalCode = async (
  data: CreateNominalCodeRequest
): Promise<{ id: number; message: string }> => {
  const response = await apiClient.post('/nominal-codes', data);
  return response.data;
};

export const updateNominalCode = async (
  codeId: number,
  data: {
    nominal_code?: string;
    description?: string;
    category?: string;
    vat_rate?: number;
    default_tax_code?: string;
    is_active?: boolean;
  }
): Promise<{ message: string }> => {
  const response = await apiClient.put(`/nominal-codes/${codeId}`, null, { params: data });
  return response.data;
};

export const deleteNominalCode = async (codeId: number): Promise<{ message: string }> => {
  const response = await apiClient.delete(`/nominal-codes/${codeId}`);
  return response.data;
};

// ============================================================================
// Knowledge Base
// ============================================================================

export const getKnowledge = async (params?: {
  company_id?: number;
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<KnowledgeListResponse> => {
  const response = await apiClient.get<KnowledgeListResponse>('/knowledge', { params });
  return response.data;
};

export const getKnowledgeEntry = async (knowledgeId: number): Promise<Knowledge> => {
  const response = await apiClient.get<Knowledge>(`/knowledge/${knowledgeId}`);
  return response.data;
};

export const getCompanyKnowledge = async (companyId: number): Promise<{
  company: Company;
  knowledge: Knowledge[];
}> => {
  const response = await apiClient.get(`/companies/${companyId}/knowledge`);
  return response.data;
};

export const createKnowledge = async (
  data: CreateKnowledgeRequest
): Promise<{ id: number; message: string }> => {
  const response = await apiClient.post('/knowledge', data);
  return response.data;
};

export const updateKnowledge = async (
  knowledgeId: number,
  data: UpdateKnowledgeRequest
): Promise<{ message: string }> => {
  const response = await apiClient.put(`/knowledge/${knowledgeId}`, data);
  return response.data;
};

export const deleteKnowledge = async (knowledgeId: number): Promise<{ message: string }> => {
  const response = await apiClient.delete(`/knowledge/${knowledgeId}`);
  return response.data;
};

// ============================================================================
// Admin - User Management
// ============================================================================

export const getAdminUsers = async (): Promise<AdminUser[]> => {
  const response = await apiClient.get<AdminUser[]>('/admin/users');
  return response.data;
};

export const createAdminUser = async (
  data: CreateUserRequest
): Promise<{ id: number; message: string }> => {
  const response = await apiClient.post('/admin/users', data);
  return response.data;
};

export const updateAdminUser = async (
  userId: number,
  data: UpdateUserRequest
): Promise<{ message: string }> => {
  const response = await apiClient.put(`/admin/users/${userId}`, data);
  return response.data;
};

export const deleteAdminUser = async (userId: number): Promise<{ message: string }> => {
  const response = await apiClient.delete(`/admin/users/${userId}`);
  return response.data;
};

export const resetUserPassword = async (
  userId: number,
  newPassword: string
): Promise<{ message: string }> => {
  const response = await apiClient.post(`/admin/users/${userId}/reset-password`, null, {
    params: { new_password: newPassword }
  });
  return response.data;
};

export const activateUser = async (userId: number): Promise<{ message: string }> => {
  const response = await apiClient.post(`/admin/users/${userId}/activate`);
  return response.data;
};

// ============================================================================
// Counterparties (Suppliers & Customers)
// ============================================================================

export const getCounterparties = async (params?: {
  company_id?: number;
  type?: CounterpartyType;
  is_active?: boolean;
  search?: string;
}): Promise<Counterparty[]> => {
  const response = await apiClient.get<Counterparty[]>('/counterparties', { params });
  return response.data;
};

export const getCompanyCounterparties = async (
  companyId: number,
  type?: CounterpartyType
): Promise<Counterparty[]> => {
  const response = await apiClient.get<Counterparty[]>(`/companies/${companyId}/counterparties`, {
    params: type ? { type } : undefined
  });
  return response.data;
};

export const getCounterparty = async (counterpartyId: number): Promise<Counterparty> => {
  const response = await apiClient.get<Counterparty>(`/counterparties/${counterpartyId}`);
  return response.data;
};

export const createCounterparty = async (
  data: CreateCounterpartyRequest
): Promise<{ id: number; message: string }> => {
  const response = await apiClient.post('/counterparties', data);
  return response.data;
};

export const updateCounterparty = async (
  counterpartyId: number,
  data: UpdateCounterpartyRequest
): Promise<{ message: string }> => {
  const response = await apiClient.put(`/counterparties/${counterpartyId}`, data);
  return response.data;
};

export const deleteCounterparty = async (counterpartyId: number): Promise<{ message: string }> => {
  const response = await apiClient.delete(`/counterparties/${counterpartyId}`);
  return response.data;
};

// ============================================================================
// Suppliers
// ============================================================================

export const getSuppliers = async (companyId?: number, status?: string): Promise<Supplier[]> => {
  const params: Record<string, any> = {};
  if (companyId) params.company_id = companyId;
  if (status) params.status = status;
  const response = await apiClient.get<Supplier[]>('/suppliers', { params });
  return response.data;
};

export const getSupplier = async (id: number): Promise<Supplier> => {
  const response = await apiClient.get<Supplier>(`/suppliers/${id}`);
  return response.data;
};

export const createSupplier = async (data: CreateSupplierRequest): Promise<{ id: number }> => {
  const response = await apiClient.post('/suppliers', data);
  return response.data;
};

export const updateSupplier = async (id: number, data: UpdateSupplierRequest): Promise<{ message: string }> => {
  const response = await apiClient.put(`/suppliers/${id}`, data);
  return response.data;
};

export const deleteSupplier = async (id: number): Promise<{ message: string }> => {
  const response = await apiClient.delete(`/suppliers/${id}`);
  return response.data;
};

export default apiClient;
